import _initializerDefineProperty from "@babel/runtime/helpers/initializerDefineProperty";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _applyDecoratedDescriptor from "@babel/runtime/helpers/applyDecoratedDescriptor";
import _initializerWarningHelper from "@babel/runtime/helpers/initializerWarningHelper";
var _class, _descriptor, _descriptor2, _descriptor3;
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbBooleanLogicTreeContext } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeContext";
import { DRAG_DROP_TRANSFER_CONTENT } from "abreact/incubator/ui/abBooleanLogicTree/AbBooleanLogicTreeDragDrop";
import { AbBooleanLogicGroupingContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicGroupingContent";
import { AbBooleanLogicSingleConditionContent } from "abreact/incubator/ui/abBooleanLogicTree/model/AbBooleanLogicSingleConditionContent";
import { AbActionMenuItem } from "abreact/incubator/ui/component/actionBar/AbActionMenuItem";
import { ActionBarMachine } from "abreact/incubator/ui/component/actionBar/ActionBarMachine";
import { ActionBarMenuMachine } from "abreact/incubator/ui/component/actionBar/ActionBarMenuMachine";
import { FocusUtils } from "abreact/incubator/ui/utils/FocusUtils";
import { useCachedValue, useIsMounted } from "abreact/utils/AbHooks";
import { AbWebServices } from "abweb/service/AbWebServices";
import { ClassUtils } from "abweb/utils/ClassUtils";
import { KeyValue } from "abweb/utils/KeyboardUtils";
import { LocaleUtils } from "abweb/utils/LocaleUtils";
import { NullUtils } from "abweb/utils/NullUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
import { action, makeObservable, observable, reaction } from "mobx";
import { useContext, useEffect, useState } from "react";
export const ABLOGICTREE_SELECTABLE_CLASS = "AbBooleanLogicTree-Selectable";
export const ABLOGICTREE_CLIPBOARD_ID_COPYPASTE = "AbBooleanLogicTreeSelectionData";
export let SelectionMachineAction;
(function (_SelectionMachineAction) {
  function isReadonly(action) {
    switch (action) {
      case SelectionMachineAction.ActionTypeEnum.COPY:
      case SelectionMachineAction.ActionTypeEnum.DRAG:
        return true;
      default:
        return false;
    }
  }
  _SelectionMachineAction.isReadonly = isReadonly;
  function clearSelectionAfterAction(action) {
    switch (action) {
      case SelectionMachineAction.ActionTypeEnum.COPY:
      case SelectionMachineAction.ActionTypeEnum.DRAG:
        return false;
      default:
        return true;
    }
  }
  _SelectionMachineAction.clearSelectionAfterAction = clearSelectionAfterAction;
  let ActionTypeEnum = /*#__PURE__*/function (ActionTypeEnum) {
    ActionTypeEnum[ActionTypeEnum["CUT"] = 0] = "CUT";
    ActionTypeEnum[ActionTypeEnum["COPY"] = 1] = "COPY";
    ActionTypeEnum[ActionTypeEnum["PASTE"] = 2] = "PASTE";
    ActionTypeEnum[ActionTypeEnum["REMOVE"] = 3] = "REMOVE";
    ActionTypeEnum[ActionTypeEnum["INDENT"] = 4] = "INDENT";
    ActionTypeEnum[ActionTypeEnum["DISABLE"] = 5] = "DISABLE";
    ActionTypeEnum[ActionTypeEnum["ENABLE"] = 6] = "ENABLE";
    ActionTypeEnum[ActionTypeEnum["DRAG"] = 7] = "DRAG";
    ActionTypeEnum[ActionTypeEnum["DROP"] = 8] = "DROP";
    return ActionTypeEnum;
  }({});
  _SelectionMachineAction.ActionTypeEnum = ActionTypeEnum;
})(SelectionMachineAction || (SelectionMachineAction = {}));
export function useAbBooleanLogicTreeSelectionMachine(props) {
  const {
    keyGenerator,
    treeContent,
    onSelectionAction
  } = {
    ...props
  };
  const selectionMachine = useCachedValue(() => onSelectionAction === undefined ? undefined : new AbBooleanLogicTreeSelectionMachine(keyGenerator, onSelectionAction, treeContent), [keyGenerator, onSelectionAction, treeContent]);
  useEffect(() => {
    if (selectionMachine) {
      selectionMachine.setTreeContent(treeContent);
    }
  }, [selectionMachine, treeContent]);
  return selectionMachine;
}
export let AbBooleanLogicTreeSelectionMachine = (_class = class AbBooleanLogicTreeSelectionMachine {
  constructor(keyGenerator, onSelectionAction, treeContent) {
    _defineProperty(this, "_actionBarMachine", void 0);
    _defineProperty(this, "_rightClickMachine", void 0);
    _initializerDefineProperty(this, "_currentSelection", _descriptor, this);
    _initializerDefineProperty(this, "_focusedSelection", _descriptor2, this);
    _initializerDefineProperty(this, "_focusedRef", _descriptor3, this);
    _defineProperty(this, "_clipboard", void 0);
    _defineProperty(this, "_keyGenerator", void 0);
    _defineProperty(this, "_addedConditionKeys", []);
    _defineProperty(this, "_addedGroupKeys", []);
    /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
     /*  Selection Machine Prop Functions ========== ========== ========== ========== ========== ==========
     /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/
    _defineProperty(this, "treeContent", void 0);
    _defineProperty(this, "_onSelectionAction", void 0);
    _defineProperty(this, "selectRange", targetKey => {
      let retSelection;
      retSelection = AbBooleanLogicGroupingContent.getItemRange(this.treeContent.nestingState, this, targetKey).selectionList;
      for (let i = 0; i < retSelection.length; i++) {
        if (!this.isSelected(retSelection[i])) {
          this.passiveSelectCondition(retSelection[i]);
        }
      }
    });
    makeObservable(this);
    this._currentSelection = [];
    this._focusedSelection = null;
    this._focusedRef = null;
    this._clipboard = AbWebServices.clipboard;
    this._keyGenerator = keyGenerator;
    this._onSelectionAction = onSelectionAction;
    this._actionBarMachine = new ActionBarMachine([]);
    this._rightClickMachine = new ActionBarMenuMachine(null, [], (item, x, y) => {
      this._actionBarMachine.performAction(item, x, y);
      this._rightClickMachine.close();
    });
    this.treeContent = treeContent;
  }
  get clipboard() {
    return this._clipboard;
  }
  getAddedConditionKeys() {
    return this._addedConditionKeys;
  }
  getAddedGroupKeys() {
    return this._addedGroupKeys;
  }
  completeSelectionAction(clearSelectionAfterAction) {
    if (clearSelectionAfterAction) {
      this.clearCurrentSelection();
    }
    this._addedGroupKeys = [];
    this._addedConditionKeys = [];
  }
  generateSaveAndReturnID(type) {
    const generatedID = this._keyGenerator();
    type === "GROUP" ? this._addedGroupKeys.push(generatedID) : this._addedConditionKeys.push(generatedID);
    return generatedID;
  }
  generateNewID() {
    return this._keyGenerator();
  }
  addConditionKeyToAdditionalInfo(key) {
    this._addedConditionKeys.push(key);
  }
  setTreeContent(treeContent) {
    this.treeContent = treeContent;
  }
  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /*  Selection Machine Sate Helpers = ========== ========== ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  get currentSelection() {
    return this._currentSelection;
  }
  get focusedRef() {
    return this._focusedRef;
  }
  get focusedSelection() {
    return this._focusedSelection;
  }
  hasSelections() {
    return this._currentSelection.length !== 0;
  }
  isSelected(key) {
    return this._currentSelection.includes(key);
  }
  isFocusedSelection(key) {
    if (this._focusedSelection !== null) {
      return key === this._focusedSelection;
    }
    return false;
  }
  conditionHasFocus(conditionKey) {
    return this._focusedSelection !== null && this._focusedSelection === conditionKey;
  }
  hasFocus() {
    return this._focusedSelection !== null;
  }
  get isValidForIndent() {
    return AbBooleanLogicGroupingContent.analyzeSelection(this.treeContent.nestingState, this).isValidForIndent;
  }
  get isSelectionFullyDisabled() {
    let foundEnabledCondition = false;
    const conditionAction = rowOrGroup => {
      if (ClassUtils.isInstanceOf(rowOrGroup, AbBooleanLogicSingleConditionContent) && this.isSelected(rowOrGroup.key) && !rowOrGroup.disabled) {
        foundEnabledCondition = true;
      }
    };
    this.treeContent.nestingState.visitAllNodes(conditionAction);
    return !foundEnabledCondition;
  }
  get isSelectionFullyEnabled() {
    let foundDisabledCondition = false;
    const conditionAction = rowOrGroup => {
      if (ClassUtils.isInstanceOf(rowOrGroup, AbBooleanLogicSingleConditionContent) && this.isSelected(rowOrGroup.key) && rowOrGroup.disabled) {
        foundDisabledCondition = true;
      }
    };
    this.treeContent.nestingState.visitAllNodes(conditionAction);
    return !foundDisabledCondition;
  }

  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /*  Selection Machine Sate Actions = ========== ========== ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  setSelection(key, el) {
    this._currentSelection = [];
    this.selectCondition(key, el);
  }
  clearCurrentSelection() {
    this._currentSelection = [];
    if (this._focusedRef) {
      this._focusedRef.blur();
    }
    this._focusedSelection = null;
  }
  deselectCondition(conditionKey) {
    if (this._currentSelection.includes(conditionKey)) {
      this._focusedSelection = null;
      this._focusedRef = null;
      this._currentSelection = this._currentSelection.filter(key => {
        return key !== conditionKey;
      });
    }
  }
  selectCondition(conditionKey, el) {
    if (!this._currentSelection.includes(conditionKey)) {
      this._currentSelection.push(conditionKey);
      this._focusedSelection = conditionKey;
      this._focusedRef = el;
      FocusUtils.setFocus(el);
    }
  }
  setFocus(conditionKey, el) {
    if (this._currentSelection.includes(conditionKey)) {
      this._focusedSelection = conditionKey;
      this._focusedRef = el;
    }
  }
  passiveSelectCondition(conditionKey) {
    if (!this._currentSelection.includes(conditionKey)) {
      this._currentSelection.push(conditionKey);
    }
  }
  clearFocus() {
    this._focusedRef?.blur();
    this._focusedSelection = null;
    this._focusedRef = null;
  }
  refreshFocus(conditionKey, el) {
    this._focusedSelection = conditionKey;
    this._focusedRef = el;
    FocusUtils.setFocus(el);
  }
  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /*  Perform On-Key-Down Behavior === ========== ========== ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  selectionOnKeyDown(event, conditionKey) {
    let target = event.target;
    if (this.isValidTarget(target)) {
      if (event.key === KeyValue.DELETE || event.key === KeyValue.BACKSPACE) {
        this.performSelectionAction({
          actionType: SelectionMachineAction.ActionTypeEnum.REMOVE,
          selectionMachine: this
        });
      } else if (event.key === KeyValue.ENTER) {
        this.selectCondition(conditionKey, target);
      } else if (event.key === KeyValue.ESCAPE) {
        this.clearCurrentSelection();
      }
    }
  }

  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /* Perform On-Click Behavior ======= ========== ========== ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  isValidTarget(target) {
    return target.classList.contains(ABLOGICTREE_SELECTABLE_CLASS) || target.classList.contains("selected-condition-single");
  }
  performSelectionOnConditionClicked(itemKey, event) {
    let target = event.target;
    if (!this.isValidTarget(target)) {
      return;
    }

    // Since Valid Click Override, we want to prevent further default behavior from executing
    event.stopPropagation(); // Don't trigger parent 'onClicks'

    if (event.ctrlKey) {
      if (this.isSelected(itemKey)) {
        this.refreshFocus(itemKey, event.currentTarget);
      } else {
        // Add this condition row to the selection, set focus on it
        this.selectCondition(itemKey, event.currentTarget);
      }
    } else if (event.shiftKey && this.hasFocus()) {
      let prevFocusKey = this.focusedSelection;
      let prevFocusRef = this.focusedRef;
      this.setSelection(prevFocusKey, prevFocusRef);
      this.selectRange(itemKey); // Does not set focus
      this.setFocus(itemKey, event.currentTarget);
    } else {
      // Set this row as the only condition selected, set focus on it
      this.setSelection(itemKey, event.currentTarget);
    }
  }
  performSelectionOnContextMenu(itemKey, event) {
    let target = event.target;
    if (!this.isValidTarget(target)) {
      return;
    }
    if (!this.isSelected(itemKey)) this.performSelectionOnConditionClicked(itemKey, event);

    // Since Valid Context Menu Override, we want to prevent further default behavior from executing
    event.preventDefault();
    let menuItems = [];
    menuItems.push(new AbActionMenuItem("absdk:arrow-right",
    // icon
    LocaleUtils.localizeMe("Indent Selection"), () => {
      this.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.INDENT,
        selectionMachine: this
      });
    }, this.isValidForIndent, null, "booltree-indent-Selection"));
    menuItems.push(new AbActionMenuItem("absdk:check",
    // icon: :edit
    LocaleUtils.localizeMe("Enable Selection"), () => {
      this.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.ENABLE,
        selectionMachine: this
      });
    }, !this.isSelectionFullyEnabled));
    menuItems.push(new AbActionMenuItem("absdk:disable",
    // icon :disable
    LocaleUtils.localizeMe("Disable Selection"), () => {
      this.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.DISABLE,
        selectionMachine: this
      });
    }, !this.isSelectionFullyDisabled, null, "booltree-disable-Selection"));
    menuItems.push(new AbActionMenuItem("absdk:delete",
    // icon
    LocaleUtils.localizeMe("Delete Selection"), () => {
      this.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.REMOVE,
        selectionMachine: this
      });
    }, true, null, "booltree-delete-Selection"));
    this._rightClickMachine.menuItems = menuItems;
    this._rightClickMachine.open(event.clientX + 15, event.clientY + 5);
  }

  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /*  Selection Action Callbacks & Handlers ===== ========== ========== ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  /** Called from AbBooleanLogicTree to instigate a selection action callback **/
  static performCut(selectionMachine) {
    return () => selectionMachine.performSelectionAction({
      actionType: SelectionMachineAction.ActionTypeEnum.CUT,
      selectionMachine: selectionMachine
    });
  }
  static performCopy(selectionMachine) {
    return () => selectionMachine.performSelectionAction({
      actionType: SelectionMachineAction.ActionTypeEnum.COPY,
      selectionMachine: selectionMachine
    });
  }
  static performPaste(selectionMachine) {
    return () => {
      selectionMachine.performSelectionAction({
        actionType: SelectionMachineAction.ActionTypeEnum.PASTE,
        selectionMachine: selectionMachine
      });
      return true;
    };
  }
  get performSelectionAction() {
    return selectionInfo => {
      let abBooleanLogicTreeAdditionalInfo = {
        selectionAction: selectionInfo
      };
      this._onSelectionAction(abBooleanLogicTreeAdditionalInfo);
    };
  }

  /**
   * Called from the Parent of the AbBooleanLogicTree to execute a selection action callback.
   * The selection action will only return model content if the model content was changed.
   */
  async executeAbBooleanLogicTreeSelectionAction(additionalInfo, currentBooleanLogicTreeContent, serialize, deserialize) {
    const selectionAction = NullUtils.assertDefined(additionalInfo.selectionAction);
    const selectionMachine = selectionAction.selectionMachine;
    let ret = undefined;
    switch (selectionAction.actionType) {
      case SelectionMachineAction.ActionTypeEnum.CUT:
        ret = await this.executeAbBooleanLogicTreeSelectionCut(currentBooleanLogicTreeContent, serialize);
        break;
      case SelectionMachineAction.ActionTypeEnum.COPY:
        await this.executeAbBooleanLogicTreeSelectionCopy(currentBooleanLogicTreeContent, serialize);
        break;
      case SelectionMachineAction.ActionTypeEnum.PASTE:
        ret = await this.executeAbBooleanLogicTreeSelectionPaste(currentBooleanLogicTreeContent, deserialize);
        break;
      case SelectionMachineAction.ActionTypeEnum.REMOVE:
        ret = this.executeAbBooleanLogicTreeSelectionRemove(currentBooleanLogicTreeContent);
        break;
      case SelectionMachineAction.ActionTypeEnum.INDENT:
        ret = this.executeAbBooleanLogicTreeSelectionIndent(currentBooleanLogicTreeContent);
        break;
      case SelectionMachineAction.ActionTypeEnum.DISABLE:
        ret = this.executeAbBooleanLogicTreeSelectionDisable(currentBooleanLogicTreeContent);
        break;
      case SelectionMachineAction.ActionTypeEnum.ENABLE:
        ret = this.executeAbBooleanLogicTreeSelectionEnable(currentBooleanLogicTreeContent);
        break;
      case SelectionMachineAction.ActionTypeEnum.DRAG:
        await this.executeAbBooleanLogicTreeSelectionDrag(currentBooleanLogicTreeContent, serialize, selectionAction.dataTransfer);
        break;
      case SelectionMachineAction.ActionTypeEnum.DROP:
        ret = await this.executeAbBooleanLogicTreeSelectionDrop(currentBooleanLogicTreeContent, deserialize, selectionAction.dataTransfer, selectionAction.dropTargetLocation);
        break;
    }
    additionalInfo.newRowKeys = selectionMachine.getAddedConditionKeys();
    additionalInfo.newGroupKeys = selectionMachine.getAddedGroupKeys();
    selectionMachine.completeSelectionAction(SelectionMachineAction.clearSelectionAfterAction(selectionAction.actionType));
    return ret;
  }

  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /*  Clipboard Helper Functions ===== ========== ========== ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  async saveSelectionDataToClipboard(toClipboard, serialize) {
    /** Save Data To Clipboard Copy Map in JSON format **/
    const clipboardJson = await serialize(toClipboard);
    this._clipboard.setData(ABLOGICTREE_CLIPBOARD_ID_COPYPASTE, clipboardJson);
  }
  async getSelectionDataFromClipboard(deserialize) {
    const clipboardData = this._clipboard.getData(ABLOGICTREE_CLIPBOARD_ID_COPYPASTE);
    if (clipboardData) {
      const deserialization = deserialize(clipboardData);
      /* Silently Ignore Failures regarding Cross-Domain serialization, this functionality is not supported yet */
      deserialization.catch(TypeUtils.emptyFunction);
      return deserialization;
    }
    return undefined;
  }

  /** ========== ========== ========== ========== ========== ========== ========== ========== ==========
   /*  Execution implementation for all selection actions === ========== ========== ========== ==========
   /** ========== ========== ========== ========== ========== ========== ========== ========== ========== **/

  async executeAbBooleanLogicTreeSelectionCut(currentBooleanLogicTreeContent, serialize) {
    const toClipboard = currentBooleanLogicTreeContent.getSelectedContent(this, currentBooleanLogicTreeContent.treeContent.nestingState);
    await this.saveSelectionDataToClipboard(toClipboard, serialize);
    return currentBooleanLogicTreeContent.removeSelectedGuidedExpressionContent(this);
  }
  executeAbBooleanLogicTreeSelectionCopy(currentBooleanLogicTreeContent, serialize) {
    return this.saveSelectionDataToClipboard(currentBooleanLogicTreeContent.getSelectedContent(this, currentBooleanLogicTreeContent.treeContent.nestingState), serialize);
  }
  async executeAbBooleanLogicTreeSelectionPaste(currentBooleanLogicTreeContent, deserialize) {
    const dataTransferBooleanLogicTreeContent = await this.getSelectionDataFromClipboard(deserialize);
    if (dataTransferBooleanLogicTreeContent === undefined || !this.hasSelections()) return currentBooleanLogicTreeContent;
    const targetKey = this.hasSelections() ? this.focusedSelection !== null ? this.focusedSelection : this.currentSelection[0] : undefined;
    if (targetKey === undefined) return currentBooleanLogicTreeContent;
    const targetLocation = currentBooleanLogicTreeContent.treeContent.nestingState.findRowLocation(targetKey);
    if (targetLocation === null) return currentBooleanLogicTreeContent;

    /** Paste behavior should insert below the targeted location **/
    targetLocation.index = targetLocation.index + 1;
    const reconciledBooleanLogicTreeContent = currentBooleanLogicTreeContent.reconcileIncomingBooleanLogicTreeContent(this, dataTransferBooleanLogicTreeContent);
    return currentBooleanLogicTreeContent.insertSelectedGuidedExpressionContent(this, targetLocation, reconciledBooleanLogicTreeContent);
  }
  executeAbBooleanLogicTreeSelectionRemove(currentBooleanLogicTreeContent) {
    if (!this.hasSelections()) return currentBooleanLogicTreeContent;
    return currentBooleanLogicTreeContent.removeSelectedGuidedExpressionContent(this);
  }
  executeAbBooleanLogicTreeSelectionIndent(currentBooleanLogicTreeContent) {
    if (!this.hasSelections()) return currentBooleanLogicTreeContent;
    return currentBooleanLogicTreeContent.indentSelectedGuidedExpressionContent(this);
  }
  executeAbBooleanLogicTreeSelectionDisable(currentBooleanLogicTreeContent) {
    if (!this.hasSelections()) return currentBooleanLogicTreeContent;
    return currentBooleanLogicTreeContent.disableOrEnableTreeContent(this, "disable");
  }
  executeAbBooleanLogicTreeSelectionEnable(currentBooleanLogicTreeContent) {
    if (!this.hasSelections()) return currentBooleanLogicTreeContent;
    return currentBooleanLogicTreeContent.disableOrEnableTreeContent(this, "enable");
  }
  async executeAbBooleanLogicTreeSelectionDrag(currentBooleanLogicTreeContent, serialize, dataTransfer) {
    const {
      nestingState
    } = currentBooleanLogicTreeContent.treeContent;
    const selectedContent = currentBooleanLogicTreeContent.getSelectedContent(this, nestingState);
    const serializedSelectedContent = await serialize(selectedContent);
    dataTransfer.setData(DRAG_DROP_TRANSFER_CONTENT, serializedSelectedContent);
  }
  async executeAbBooleanLogicTreeSelectionDrop(currentBooleanLogicTreeContent, deserialize, dataTransfer, dropTargetLocation) {
    const jsonRepresentation = dataTransfer.getData(DRAG_DROP_TRANSFER_CONTENT);
    const dataTransferBooleanLogicTreeContent = jsonRepresentation !== "" ? await deserialize(jsonRepresentation) : undefined;
    if (dataTransferBooleanLogicTreeContent === undefined) {
      return currentBooleanLogicTreeContent;
    }
    const reconciledBooleanTreeContent = currentBooleanLogicTreeContent.reconcileIncomingBooleanLogicTreeContent(this, dataTransferBooleanLogicTreeContent);
    return currentBooleanLogicTreeContent.moveSelectedGuidedExpressionContent(this, dropTargetLocation, reconciledBooleanTreeContent);
  }

  /** Create and use a transient HTML Canvas element for display in the drag-action **/
  static getDragDisplayElement(selectionMachine) {
    const canvas = document.createElement("canvas");
    canvas.className = "logicTreeDragElement";
    canvas.style.position = "absolute";
    canvas.style.left = "-100%"; /* hide it */
    canvas.style.borderRadius = "6px";
    document.body.append(canvas);
    canvas.height = 20;
    canvas.width = 70;
    const context = canvas.getContext("2d");
    if (context != null) {
      // Use lighter version of the color used for the 'Selected' CSS
      // --ab-global-color-blue-100: #d9eafb;
      context.fillStyle = "#d9eafb";
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = "black";
      context.font = "14px Roboto";
      context.textAlign = "center";
      context.textBaseline = "middle";
      const length = selectionMachine?.currentSelection.length ?? 0;
      const text = length > 1 ? "rows" : "row";
      context.fillText(`${selectionMachine?.currentSelection.length} ${text}`, canvas.width / 2, canvas.height / 2, canvas.width);
    }
    return canvas;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "_currentSelection", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "_focusedSelection", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "_focusedRef", [observable], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "setSelection", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setSelection"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearCurrentSelection", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearCurrentSelection"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "deselectCondition", [action], Object.getOwnPropertyDescriptor(_class.prototype, "deselectCondition"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectCondition", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectCondition"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setFocus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setFocus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "passiveSelectCondition", [action], Object.getOwnPropertyDescriptor(_class.prototype, "passiveSelectCondition"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "clearFocus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "clearFocus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "refreshFocus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "refreshFocus"), _class.prototype), _class);
export function useRegisterSelectionTarget(props) {
  const context = NullUtils.assertDefined(useContext(AbBooleanLogicTreeContext));
  const selectionMachine = context.selectionMachine;
  const isMounted = useIsMounted();
  const [isSelected, setIsSelected] = useState(() => selectionMachine?.isSelected(props.conditionKey) ?? false);
  const [isFocusedSelection, setIsFocusedSelection] = useState(() => selectionMachine?.conditionHasFocus(props.conditionKey) ?? false);
  useEffect(() => {
    const initializationTriggerDisposers = [];
    if (selectionMachine && isMounted.current) {
      initializationTriggerDisposers.push(reaction(() => selectionMachine.isSelected(props.conditionKey), isCurrentTarget => {
        setIsSelected(isCurrentTarget);
      }, {
        fireImmediately: true
      }));
      initializationTriggerDisposers.push(reaction(() => selectionMachine.isFocusedSelection(props.conditionKey), isFocusedSelection => {
        setIsFocusedSelection(isFocusedSelection);
      }, {
        fireImmediately: true
      }));
    }
    return () => {
      initializationTriggerDisposers.forEach(dispose => dispose());
    };
  }, [context.selectionMachine, props.conditionKey]);
  if (selectionMachine == null) {
    return undefined;
  }
  return {
    isSelected: isSelected,
    isFocusedSelection: isFocusedSelection
  };
}
//# sourceMappingURL=AbBooleanLogicTreeSelectionMachine.js.map
